/**
 * RoleGuard -- route-level access control.
 *
 * Wraps a route's element. If the current user's role doesn't meet
 * the minimum requirement, shows an "access denied" message instead
 * of the page content. Does NOT redirect (so the URL stays correct
 * and the user understands why they can't access the page).
 */
import { type ReactNode } from "react";

import { ShieldX } from "lucide-react";

import { useRoleAccess } from "@/features/auth/model/useRoleAccess";

interface RoleGuardProps {
  /** The minimum role required to see this page (linear hierarchy check) */
  minimum?: string;
  /**
   * Explicit allowed-roles list. Used when the linear hierarchy
   * doesn't fit - e.g., Scheduling is for operational roles (Staff down
   * to Volunteer) but NOT for Board (a lateral governance role that sits
   * "above" Volunteer in the hierarchy but shouldn't see ops pages).
   *
   * If both minimum and allowedRoles are set, allowedRoles wins.
   */
  allowedRoles?: string[];
  /** Human-readable name of the page (for the error message) */
  label: string;
  /** The page content to render if authorized */
  children: ReactNode;
}

export function RoleGuard({ minimum, allowedRoles, label, children }: RoleGuardProps) {
  const { hasMinRole, role } = useRoleAccess();

  const allowed = allowedRoles
    ? allowedRoles.includes(role || "")
    : minimum
      ? hasMinRole(minimum)
      : false;

  if (!allowed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <ShieldX className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold mb-2">
          You don't have access to {label}
        </h2>
        <p className="text-sm text-muted-foreground max-w-md">
          This page requires a higher access level. Your current role
          is <strong>{role}</strong>. Contact your organization's
          administrator if you need access.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
